import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Plus, Check, X } from 'lucide-react';

// Shared look for every dropdown / multi-select / matching text input.
export const dropdownPalette = (theme) => {
  const isDark = theme === 'dark';
  return {
    primary: '#2e7cba',
    primarySoft: isDark ? 'rgba(46, 124, 186, 0.22)' : '#eef5fb',
    ring: isDark ? 'rgba(46, 124, 186, 0.35)' : 'rgba(46, 124, 186, 0.18)',
    text: isDark ? '#f1f5f9' : '#0f172a',
    muted: isDark ? '#94a3b8' : '#64748b',
    placeholder: isDark ? '#64748b' : '#94a3b8',
    border: isDark ? 'rgba(148, 163, 184, 0.28)' : '#cbd5e1',
    borderHover: isDark ? 'rgba(148, 163, 184, 0.5)' : '#94a3b8',
    inputBg: isDark ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
    disabledBg: isDark ? 'rgba(15, 23, 42, 0.3)' : '#f1f5f9',
    menuBg: isDark ? '#0f172a' : '#ffffff',
    menuBorder: isDark ? 'rgba(148, 163, 184, 0.24)' : '#e2e8f0',
    menuShadow: isDark
      ? '0 16px 40px rgba(0, 0, 0, 0.45)'
      : '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06)',
    hover: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
    divider: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
  };
};

// Scrollbar + placeholder rules shared by the dropdown menus.
export const DropdownGlobalStyles = ({ theme }) => {
  const p = dropdownPalette(theme);
  return (
    <style>{`
      .reusable-dropdown-portal { scrollbar-width: thin; scrollbar-color: ${p.border} transparent; }
      .reusable-dropdown-portal::-webkit-scrollbar { width: 8px; }
      .reusable-dropdown-portal::-webkit-scrollbar-track { background: transparent; }
      .reusable-dropdown-portal::-webkit-scrollbar-thumb { background: ${p.border}; border-radius: 999px; border: 2px solid transparent; background-clip: content-box; }
      [data-reusable-dropdown="true"] input::placeholder { color: ${p.placeholder}; opacity: 1; }
      @keyframes rdd-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
    `}</style>
  );
};

const MENU_MAX_HEIGHT = 300;
const ITEM_HEIGHT = 40;

const ReusableDropdown = ({
  items = [],
  value = '',
  onChange,
  onSelect,
  placeholder = 'Type to search...',
  emptyMessage,
  maxItems = 8,
  getItemKey = (item) => item.id || item.email,
  getItemLabel = (item) => item.name || item.email,
  filterFunction,
  renderItem,
  theme = 'light',
  language = 'en',
  disabled = false,
  inputStyle = {},
  dropdownStyle = {},
  itemStyle = {},
  showAllOnArrowClick = true,
  // false → behaves like a plain <select>: click to open, no typing.
  searchable = true,
  // Clear (×) button: shown by default whenever onClear is given and a value is set.
  onClear,
  showClear,
  // Forwarded to the inner input for tests / a11y.
  id,
  name,
  ariaLabel,
  // ── Creatable support (opt-in) ──────────────────────────────────────
  // When true and the typed search term has no exact (case-insensitive)
  // match among `items`, a synthetic "+ Add ..." row is appended.
  // Selecting it calls `onCreate(searchTerm)`, which must return the
  // created item (or null/undefined to cancel — e.g. on error).
  allowCreate = false,
  onCreate,
  createLabel,
  // ── Server-side search (opt-in) ─────────────────────────────────────
  // Called with the typed text whenever it changes (and '' when it is
  // cleared), so the caller can fetch matching `items` from the server.
  // Pair with `filterFunction={() => true}` so those items are not
  // filtered a second time here.
  onSearchChange,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const [creating, setCreating] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [displayedValue, setDisplayedValue] = useState(value ?? ''); // Track displayed value
  const containerRef = useRef(null);
  const portalRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [dropdownCoords, setDropdownCoords] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const isRTL = language === 'ar';
  const p = dropdownPalette(theme);
  const resolvedEmptyMessage = emptyMessage || (isRTL ? 'لا توجد نتائج' : 'No results found');

  // Sync displayedValue when value prop changes
  useEffect(() => {
    setDisplayedValue(value ?? '');
  }, [value]);

  const defaultFilterFunction = (item, searchTerm) => {
    const label = String(getItemLabel(item) || '').toLowerCase();
    const search = String(searchTerm || '').toLowerCase();
    return label.includes(search);
  };

  const withCreateRow = (list) => {
    const trimmed = String(searchTerm || '').trim();
    if (!allowCreate || !trimmed) return list;
    const exists = (items || []).some(
      (item) => String(getItemLabel(item) || '').trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) return list;
    return [...list, { __isCreateNew: true, __searchTerm: trimmed }];
  };

  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  useEffect(() => {
    onSearchChangeRef.current?.(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    // No search term: show the complete list (the dropdown body scrolls)
    if (!String(searchTerm || '').trim()) {
      setFilteredItems(items || []);
      setHighlightedIndex(-1);
      return;
    }

    const filterFunc = filterFunction || defaultFilterFunction;
    const filtered = (items || []).filter(item => filterFunc(item, searchTerm));
    setFilteredItems(withCreateRow(filtered));
    setHighlightedIndex(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, items, filterFunction, getItemLabel, allowCreate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const container = containerRef.current;
      const portal = portalRef.current;
      const clickedInsideContainer = container && container.contains(event.target);
      const clickedInsidePortal = portal && portal.contains(event.target);
      if (!clickedInsideContainer && !clickedInsidePortal) {
        setShowDropdown(false);
        setSearchTerm(''); // Clear search when closing dropdown
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isSelectedItem = (item) => {
    if (!item || item.__isCreateNew) return false;
    const v = displayedValue;
    if (v === '' || v === null || v === undefined) return false;
    return String(getItemKey(item)) === String(v) || String(getItemLabel(item)) === String(v);
  };

  // compute dropdown coordinates for portal rendering to escape stacking contexts
  const updateDropdownCoords = () => {
    const inputEl = inputRef.current;
    if (!inputEl) return;
    const rect = inputEl.getBoundingClientRect();
    const headerHeight = String(searchTerm || '').trim() ? 32 : 0;
    const rows = Math.max(filteredItems.length, 1);
    const estimatedHeight = Math.min(
      parseInt(dropdownStyle.maxHeight, 10) || MENU_MAX_HEIGHT,
      headerHeight + rows * ITEM_HEIGHT + 12,
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < estimatedHeight + 8 && spaceAbove > spaceBelow;

    setDropdownDirection(openUp ? 'up' : 'down');

    setDropdownCoords({
      // for fixed positioning we use viewport coordinates
      top: openUp ? (rect.top - estimatedHeight - 6) : rect.bottom + 6,
      left: rect.left,
      width: rect.width,
      height: estimatedHeight,
    });
  };

  useLayoutEffect(() => {
    if (!showDropdown) {
      // drop stale coords so a reopen can't flash the menu at an old position
      setDropdownCoords(null);
      return;
    }
    updateDropdownCoords();
    const handler = (e) => {
      // scrolling INSIDE the list must not reposition (it re-renders every frame)
      if (e && e.target && portalRef.current && portalRef.current.contains(e.target)) return;
      updateDropdownCoords();
    };
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDropdown, filteredItems.length]);

  // Opening the menu scrolls the current selection into view.
  useEffect(() => {
    if (!showDropdown || !dropdownCoords || !listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    if (selected && selected.scrollIntoView) selected.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDropdown, !!dropdownCoords]);

  // Keep the keyboard-highlighted row visible.
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const handleKeyDown = (e) => {
    if (!showDropdown && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !searchable) {
      e.preventDefault();
      setShowDropdown(true);
      return;
    }
    if (!showDropdown || filteredItems.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredItems.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredItems.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) handleSelect(filteredItems[highlightedIndex]);
        else if (filteredItems.length === 1) handleSelect(filteredItems[0]);
        break;
      case 'Escape':
        setShowDropdown(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setShowDropdown(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  const commitSelection = (item) => {
    // Send the item's id to onChange, not the label
    const itemId = getItemKey(item);
    setDisplayedValue(itemId); // Update displayed value immediately
    if (onChange) onChange(itemId);
    if (onSelect) onSelect(item);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setSearchTerm(''); // Clear search term after selection
    if (inputRef.current) {
      inputRef.current.blur(); // Remove focus from input
    }
  };

  const handleSelect = async (item) => {
    if (item && item.__isCreateNew) {
      if (!onCreate || creating) return;
      setCreating(true);
      try {
        const created = await onCreate(item.__searchTerm);
        if (created) commitSelection(created);
      } finally {
        setCreating(false);
      }
      return;
    }
    commitSelection(item);
  };

  const handleInputChange = (e) => {
    if (!searchable) return;
    const newValue = e.target.value;
    // Update search term for filtering
    setSearchTerm(newValue);
    setShowDropdown(true);
  };

  const handleInputFocus = () => {
    // open dropdown on focus so user sees suggestions immediately
    if (searchable) setShowDropdown(true);
  };

  const handleInputMouseDown = () => {
    // non-searchable inputs toggle like a native select
    if (disabled || searchable) return;
    setShowDropdown((open) => !open);
  };

  // Toggle dropdown open/close when arrow is clicked
  const handleArrowClick = () => {
    if (disabled) return;
    if (showDropdown) {
      setShowDropdown(false);
      setSearchTerm('');
    } else {
      setShowDropdown(true);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDisplayedValue('');
    setSearchTerm('');
    setShowDropdown(false);
    if (onClear) onClear();
  };

  // Resolve the label for the current selection. Callers are inconsistent about
  // what `value` holds (item key for object items, the label itself for string
  // items), and ids may be numbers on items but strings from the parent — so
  // compare as strings and fall back from key match to label match to raw value.
  const resolveSelectedLabel = () => {
    const v = displayedValue;
    if (v === '' || v === null || v === undefined) return '';
    const list = items || [];
    const byKey = list.find(item => String(getItemKey(item)) === String(v));
    if (byKey) return getItemLabel(byKey);
    const byLabel = list.find(item => String(getItemLabel(item)) === String(v));
    if (byLabel) return getItemLabel(byLabel);
    // items still loading: don't flash a raw id
    if (list.length === 0) return '';
    return String(v);
  };

  const selectedLabel = resolveSelectedLabel();
  const hasValue = selectedLabel !== '';
  const clearVisible = (showClear ?? !!onClear) && hasValue && !disabled && !!onClear;

  const baseInputStyle = {
    width: '100%',
    minHeight: '44px',
    padding: '10px 14px',
    paddingInlineEnd: clearVisible ? '68px' : '42px',
    borderRadius: '10px',
    border: `1px solid ${showDropdown ? p.primary : hovered && !disabled ? p.borderHover : p.border}`,
    boxShadow: showDropdown ? `0 0 0 3px ${p.ring}` : 'none',
    background: disabled ? p.disabledBg : p.inputBg,
    color: p.text,
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    textAlign: 'start',
    direction: isRTL ? 'rtl' : 'ltr',
    boxSizing: 'border-box',
    cursor: disabled ? 'not-allowed' : searchable ? 'text' : 'pointer',
    caretColor: searchable ? undefined : 'transparent',
    opacity: disabled ? 0.65 : 1,
    textOverflow: 'ellipsis',
    ...inputStyle,
  };
  // Always keep room for the clear/arrow buttons, even when a caller's
  // inputStyle sets a `padding` shorthand.
  delete baseInputStyle.paddingLeft;
  delete baseInputStyle.paddingRight;
  baseInputStyle.paddingInlineStart = inputStyle.paddingInlineStart ?? '14px';
  baseInputStyle.paddingInlineEnd = clearVisible ? '68px' : '42px';
  // A caller-supplied full border/background still wins, but keep the focus
  // ring so every dropdown gives the same "I'm open" feedback.
  if (showDropdown) {
    baseInputStyle.borderColor = p.primary;
    baseInputStyle.boxShadow = `0 0 0 3px ${p.ring}`;
  }

  const menuStyle = {
    background: p.menuBg,
    border: `1px solid ${p.menuBorder}`,
    boxShadow: p.menuShadow,
    borderRadius: '12px',
    zIndex: 99999,
    maxHeight: `${MENU_MAX_HEIGHT}px`,
    overflowY: 'auto',
    padding: '6px',
    boxSizing: 'border-box',
    direction: isRTL ? 'rtl' : 'ltr',
    animation: 'rdd-in 0.12s ease-out',
    ...dropdownStyle,
  };

  const baseItemStyle = {
    minHeight: `${ITEM_HEIGHT - 4}px`,
    padding: '9px 10px',
    margin: '1px 0',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: p.text,
    transition: 'background 0.1s ease',
    textAlign: 'start',
    ...itemStyle,
  };

  const defaultRenderItem = (item) => (
    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {getItemLabel(item)}
    </div>
  );

  // While open, the input is purely a search box (the selection shows as the
  // placeholder); while closed, it displays the selected label. Mixing the two
  // made typing edit the selected label text and break filtering.
  const getDisplayText = () => (showDropdown && searchable ? searchTerm : selectedLabel);
  const isSearching = !!String(searchTerm || '').trim();

  const iconButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: p.muted,
    width: '26px',
    height: '26px',
    borderRadius: '6px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.15s ease, background 0.15s ease, transform 0.2s ease',
  };

  return (
    <div
      ref={containerRef}
      data-reusable-dropdown="true"
      style={{ position: 'relative', width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete={searchable ? 'list' : 'none'}
          aria-label={ariaLabel}
          value={getDisplayText()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onMouseDown={handleInputMouseDown}
          onKeyDown={handleKeyDown}
          placeholder={showDropdown && searchable ? (selectedLabel || placeholder) : placeholder}
          disabled={disabled}
          readOnly={!searchable}
          autoComplete="off"
          style={baseInputStyle}
        />

        <div style={{
          position: 'absolute',
          insetInlineEnd: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          gap: '2px',
          alignItems: 'center',
        }}>
          {clearVisible && (
            <button
              type="button"
              aria-label={isRTL ? 'مسح' : 'Clear'}
              onMouseDown={handleClear}
              style={iconButtonStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = p.hover; e.currentTarget.style.color = p.text; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = p.muted; }}
            >
              <X size={15} />
            </button>
          )}
          {showAllOnArrowClick && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={isRTL ? 'فتح القائمة' : 'Toggle options'}
              onMouseDown={(e) => { e.preventDefault(); handleArrowClick(); }}
              disabled={disabled}
              style={{
                ...iconButtonStyle,
                color: showDropdown ? p.primary : p.muted,
                transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronDown size={18} />
            </button>
          )}
        </div>
      </div>

      {showDropdown && (typeof document !== 'undefined' && dropdownCoords) && createPortal(
        // render dropdown in a portal so it escapes parent stacking contexts
        <div
          ref={portalRef}
          className="reusable-dropdown-portal"
          role="listbox"
          style={{
            ...menuStyle,
            position: 'fixed',
            top: dropdownCoords.top + 'px',
            left: dropdownCoords.left + 'px',
            width: dropdownCoords.width + 'px',
            transformOrigin: dropdownDirection === 'down' ? 'top' : 'bottom',
          }}
        >
          <div ref={listRef}>
            {isSearching && filteredItems.length > 0 && (
              <div style={{
                padding: '6px 10px 8px',
                fontSize: '12px',
                color: p.muted,
                fontWeight: 600,
              }}>
                {filteredItems.length} {isRTL ? 'نتيجة' : filteredItems.length === 1 ? 'result' : 'results'}
              </div>
            )}

            {filteredItems.length === 0 && (
              <div style={{ padding: '18px 12px', textAlign: 'center', color: p.muted, fontSize: '14px' }}>
                {isSearching ? resolvedEmptyMessage : (isRTL ? 'لا توجد خيارات' : 'No options')}
              </div>
            )}

            {filteredItems.map((item, index) => {
              const isHighlighted = index === highlightedIndex;
              const isCreate = item && item.__isCreateNew;
              const isSelected = isSelectedItem(item);
              const itemKey = isCreate ? '__create_new__' : getItemKey(item);
              return (
                <div
                  key={itemKey}
                  role="option"
                  aria-selected={isSelected}
                  data-index={index}
                  data-selected={isSelected}
                  style={{
                    ...baseItemStyle,
                    background: isSelected
                      ? p.primarySoft
                      : isHighlighted ? p.hover : 'transparent',
                    opacity: isCreate && creating ? 0.6 : 1,
                    cursor: isCreate && creating ? 'wait' : 'pointer',
                    color: isCreate || isSelected ? p.primary : baseItemStyle.color,
                    fontWeight: isCreate || isSelected ? 600 : 500,
                    ...(isCreate && { borderTop: `1px solid ${p.divider}`, borderRadius: '0 0 8px 8px' }),
                  }}
                  onMouseDown={(e) => {
                    // select on mousedown (with preventDefault) so the input's
                    // blur/reposition can't swallow the first click
                    e.preventDefault();
                    handleSelect(item);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {isCreate ? (
                    <>
                      <Plus size={15} />
                      <span>
                        {createLabel
                          ? createLabel(item.__searchTerm)
                          : (isRTL ? `إضافة "${item.__searchTerm}"` : `Add "${item.__searchTerm}"`)}
                      </span>
                    </>
                  ) : (
                    <>
                      {renderItem ? (
                        <div style={{ flex: 1, minWidth: 0 }}>{renderItem(item, theme, language)}</div>
                      ) : defaultRenderItem(item)}
                      {isSelected && <Check size={16} style={{ flexShrink: 0, color: p.primary }} />}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      <DropdownGlobalStyles theme={theme} />
    </div>
  );
};

// Named export: unified control style for inputs to match dropdown appearance
export const unifiedControlStyle = (theme, isRTL = false) => {
  const p = dropdownPalette(theme);
  return {
    width: '100%',
    minHeight: '44px',
    padding: '10px 14px',
    borderRadius: '10px',
    border: `1px solid ${p.border}`,
    background: p.inputBg,
    color: p.text,
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    textAlign: isRTL ? 'right' : 'left',
    boxSizing: 'border-box',
  };
};

export default ReusableDropdown;
